import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { Event } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useCompanyData,
  useContentToken,
  useSlotGroup,
  useVibesiteEvents,
  EditableToken,
} from '@vibesites/runtime/react';
import { MapPin, Phone } from 'lucide-react';
import { getCommunityProperties } from './propertyUtils';

const office1Def: SlotGroupDefinition = {
  label: 'Office 1',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Tampa' },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: '4890 W Kennedy Blvd, Ste 900, Tampa, FL 33609',
    },
    phone: { label: 'Phone', type: 'phone', defaultValue: '813-252-4290' },
    note: { label: 'Note', type: 'text', defaultValue: '' },
  },
};

const office2Def: SlotGroupDefinition = {
  label: 'Office 2',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'North Palm Beach' },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: '1331 South Killian Drive, Ste A, Lake Park, FL 33403',
    },
    phone: { label: 'Phone', type: 'phone', defaultValue: '561-408-3442' },
    note: { label: 'Note', type: 'text', defaultValue: '' },
  },
};

const office3Def: SlotGroupDefinition = {
  label: 'Office 3',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'New York City' },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: '1 Rockefeller Plaza, Unit 1650, New York, NY 10020',
    },
    phone: { label: 'Phone', type: 'phone', defaultValue: '' },
    note: { label: 'Note', type: 'text', defaultValue: '' },
  },
};

const office4Def: SlotGroupDefinition = {
  label: 'Office 4',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Dubai' },
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: 'Emirates Financial North Towers, 704B, DIFC',
    },
    phone: { label: 'Phone', type: 'phone', defaultValue: '' },
    note: {
      label: 'Note',
      type: 'text',
      defaultValue:
        'American Landmark is represented in the United Arab Emirates by American Landmark Capital DIF and is regulated by the Dubai Financial Services Authority.',
    },
  },
};

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const inputClass =
  'w-full rounded-lg border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] px-4 py-3 font-body text-base text-[rgb(var(--ala-text))] outline-none transition placeholder:text-[rgb(var(--ala-text-muted))]/70 focus:border-[rgb(var(--ala-secondary))] focus:ring-2 focus:ring-[rgb(var(--ala-secondary))]/20';

const labelClass = 'mb-2 block font-heading text-sm font-semibold text-[rgb(var(--ala-text))]';

function OfficeBody({
  office,
}: {
  office: { name?: string | null; address?: string | null; phone?: string | null; note?: string | null };
}) {
  const phoneDigits = (office.phone || '').replace(/\D/g, '');

  return (
    <div>
      {office.name ? (
        <p className="mb-2 font-heading text-base font-bold text-[rgb(var(--ala-text))]">{office.name}</p>
      ) : null}
      {office.address ? (
        <p className="flex items-start gap-2 font-body text-sm leading-relaxed text-[rgb(var(--ala-text-muted))]">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--ala-accent))]" aria-hidden />
          <span>{office.address}</span>
        </p>
      ) : null}
      {office.phone ? (
        <a
          href={`tel:${phoneDigits}`}
          className="mt-2 flex items-center gap-2 font-body text-sm text-[rgb(var(--ala-text-muted))] transition hover:text-[rgb(var(--ala-text))]"
        >
          <Phone className="h-4 w-4 shrink-0 text-[rgb(var(--ala-accent))]" aria-hidden />
          <span>P: {office.phone}</span>
        </a>
      ) : null}
      {office.note ? (
        <p className="mt-3 font-body text-xs leading-relaxed text-[rgb(var(--ala-text-muted))]/80">{office.note}</p>
      ) : null}
    </div>
  );
}

function OfficeDivider({ children }: { children: ReactNode }) {
  return (
    <div className="border-b border-[rgb(var(--ala-border))] py-6 first:pt-0 last:border-b-0 last:pb-0">
      {children}
    </div>
  );
}

export function ContactForm() {
  const companyData = useCompanyData();
  const { emit } = useVibesiteEvents();
  const contactEmail = useContentToken('contactform.email');
  const office1 = useSlotGroup('contactform.office_1', office1Def);
  const office2 = useSlotGroup('contactform.office_2', office2Def);
  const office3 = useSlotGroup('contactform.office_3', office3Def);
  const office4 = useSlotGroup('contactform.office_4', office4Def);

  const communities = useMemo(() => {
    return getCommunityProperties(companyData)
      .map((p) => p.property_name || '')
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [companyData]);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === 'submitting') return;

    setStatus('submitting');
    setErrorMessage('');

    const nameParts = name.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || firstName;
    const payload: Record<string, unknown> = {
      first_name: firstName,
      last_name: lastName,
      email: email.trim(),
      phone_no: phone.trim(),
      comments: [location ? `Community: ${location}` : '', message.trim()].filter(Boolean).join('\n\n'),
      message: message.trim(),
    };

    try {
      const leads = (
        window as unknown as {
          __mh_leads__?: {
            submitAsync?: (
              data: Record<string, unknown>,
              opts?: object,
            ) => Promise<{ success?: boolean }>;
          };
        }
      ).__mh_leads__;

      if (leads?.submitAsync) {
        const response = await leads.submitAsync(payload, {});
        if (response?.success !== true) {
          throw new Error('Lead submission failed');
        }
      } else if (contactEmail) {
        const subject = encodeURIComponent(`Contact form — ${name.trim() || 'Website inquiry'}`);
        const body = encodeURIComponent(
          [
            `Name: ${name.trim()}`,
            `Email: ${email.trim()}`,
            `Phone: ${phone.trim()}`,
            location ? `Community: ${location}` : '',
            '',
            message.trim(),
          ]
            .filter((line, i, arr) => !(line === '' && arr[i - 1] === ''))
            .join('\n'),
        );
        window.location.href = `mailto:${contactEmail}?subject=${subject}&body=${body}`;
      } else {
        throw new Error('Form is not configured');
      }

      try {
        emit(Event.contact_form_submitted, {
          sectionId: 'contact-form',
          formId: 'contact-form',
        });
      } catch {
        // Tracking must never break form UX.
      }

      setStatus('success');
      setName('');
      setEmail('');
      setPhone('');
      setLocation('');
      setMessage('');
    } catch {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again or email us directly.');
    }
  }

  return (
    <section className="bg-[#f3f5f7] px-6 py-16 md:px-12 md:py-24">
      <div className="mx-auto max-w-[1200px]">
        <div className="overflow-hidden rounded-2xl bg-[rgb(var(--ala-background))] shadow-[0_12px_48px_rgba(10,22,38,0.1)]">
          <div className="grid lg:grid-cols-[minmax(0,1.7fr)_minmax(280px,1fr)]">
            <div className="p-8 md:p-10 lg:p-12">
              <RichTextContent
                path="contactform.heading"
                label="Heading"
                as="h2"
                className="font-heading text-[clamp(1.75rem,3vw,2.25rem)] font-bold tracking-[-0.02em] text-[rgb(var(--ala-text))]"
              />
              <RichTextContent
                path="contactform.intro"
                label="Intro"
                as="div"
                className="mt-4 max-w-[46rem] font-body text-base leading-relaxed text-[rgb(var(--ala-text-muted))] [&_p]:mb-0"
              />

              <div className="sr-only">
                <EditableToken path="contactform.email" as="span">
                  {contactEmail}
                </EditableToken>
              </div>

              {status === 'success' ? (
                <div className="mt-10 rounded-xl bg-[rgb(var(--ala-surface))] px-6 py-10 text-center">
                  <p className="font-heading text-xl font-semibold text-[rgb(var(--ala-text))]">Thank you!</p>
                  <p className="mt-2 font-body text-[rgb(var(--ala-text-muted))]">
                    We received your message and will be in touch during business hours.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="mt-6 font-heading text-sm font-semibold uppercase tracking-wider text-[rgb(var(--ala-secondary))] transition hover:text-[rgb(var(--ala-primary))]"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form className="mt-10 space-y-5" onSubmit={handleSubmit} noValidate>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-name" className={labelClass}>
                        Your Name
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        autoComplete="name"
                        placeholder="Jane Doe"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={status === 'submitting'}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className={labelClass}>
                        Email Address
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        placeholder="jane@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={status === 'submitting'}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-phone" className={labelClass}>
                        Phone Number
                      </label>
                      <input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        autoComplete="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        disabled={status === 'submitting'}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-location" className={labelClass}>
                        Choose Location
                      </label>
                      <div className="relative">
                        <select
                          id="contact-location"
                          name="location"
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                          disabled={status === 'submitting'}
                          className={`${inputClass} appearance-none pr-10`}
                        >
                          <option value="">Select a community</option>
                          {communities.map((community) => (
                            <option key={community} value={community}>
                              {community}
                            </option>
                          ))}
                        </select>
                        <svg
                          viewBox="0 0 20 20"
                          fill="none"
                          className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[rgb(var(--ala-text-muted))]"
                          aria-hidden
                        >
                          <path
                            d="M5 7.5L10 12.5L15 7.5"
                            stroke="currentColor"
                            strokeWidth="1.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-message" className={labelClass}>
                      Message
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={5}
                      placeholder="How can we help you?"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      disabled={status === 'submitting'}
                      className={`${inputClass} min-h-[140px] resize-y`}
                    />
                  </div>

                  {status === 'error' && errorMessage ? (
                    <p className="font-body text-sm text-[rgb(var(--ala-accent))]" role="alert">
                      {errorMessage}
                    </p>
                  ) : null}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full rounded-lg bg-[rgb(var(--ala-secondary))] px-6 py-4 font-heading text-sm font-bold uppercase tracking-[0.12em] text-[rgb(var(--ala-on-secondary))] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {status === 'submitting' ? 'Sending…' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>

            <aside className="border-t border-[rgb(var(--ala-border))] bg-[#f7f8f9] p-8 md:p-10 lg:border-l lg:border-t-0 lg:p-10">
              <RichTextContent
                path="contactform.offices_heading"
                label="Offices Heading"
                as="h3"
                className="font-heading text-xl font-bold text-[rgb(var(--ala-text))]"
              />
              <RichTextContent
                path="contactform.offices_subtitle"
                label="Offices Subtitle"
                as="p"
                className="mt-1 font-body text-sm text-[rgb(var(--ala-text-muted))]"
              />

              <div className="mt-6">
                <OfficeDivider>
                  <EditableSlotGroup groupPath="contactform.office_1" definition={office1Def}>
                    <OfficeBody office={office1} />
                  </EditableSlotGroup>
                </OfficeDivider>
                <OfficeDivider>
                  <EditableSlotGroup groupPath="contactform.office_2" definition={office2Def}>
                    <OfficeBody office={office2} />
                  </EditableSlotGroup>
                </OfficeDivider>
                <OfficeDivider>
                  <EditableSlotGroup groupPath="contactform.office_3" definition={office3Def}>
                    <OfficeBody office={office3} />
                  </EditableSlotGroup>
                </OfficeDivider>
                <OfficeDivider>
                  <EditableSlotGroup groupPath="contactform.office_4" definition={office4Def}>
                    <OfficeBody office={office4} />
                  </EditableSlotGroup>
                </OfficeDivider>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Send us a message</p>',
    },
    'contactform.intro': {
      label: 'Intro',
      type: 'richtext',
      defaultValue:
        "<p>At American Landmark Apartments, we understand that communication is key to building quality relationships with our residents and associates. If you have feedback you'd like to share, we'd love to hear it! Fill out this form and we'll be sure to get someone in contact during business hours Monday through Friday.</p>",
    },
    'contactform.email': {
      label: 'Contact Email',
      type: 'email',
      defaultValue: 'info@alapts.com',
    },
    'contactform.offices_heading': {
      label: 'Offices Heading',
      type: 'richtext',
      defaultValue: '<p>Our Offices</p>',
    },
    'contactform.offices_subtitle': {
      label: 'Offices Subtitle',
      type: 'richtext',
      defaultValue: '<p>Reach out to any of our regional teams.</p>',
    },
  },
  slotGroups: {
    'contactform.office_1': {
      label: 'Office 1',
      fields: {
        name: { label: 'Name', type: 'text', defaultValue: 'Tampa' },
        address: {
          label: 'Address',
          type: 'text',
          defaultValue: '4890 W Kennedy Blvd, Ste 900, Tampa, FL 33609',
        },
        phone: { label: 'Phone', type: 'phone', defaultValue: '813-252-4290' },
        note: { label: 'Note', type: 'text', defaultValue: '' },
      },
    },
    'contactform.office_2': {
      label: 'Office 2',
      fields: {
        name: { label: 'Name', type: 'text', defaultValue: 'North Palm Beach' },
        address: {
          label: 'Address',
          type: 'text',
          defaultValue: '1331 South Killian Drive, Ste A, Lake Park, FL 33403',
        },
        phone: { label: 'Phone', type: 'phone', defaultValue: '561-408-3442' },
        note: { label: 'Note', type: 'text', defaultValue: '' },
      },
    },
    'contactform.office_3': {
      label: 'Office 3',
      fields: {
        name: { label: 'Name', type: 'text', defaultValue: 'New York City' },
        address: {
          label: 'Address',
          type: 'text',
          defaultValue: '1 Rockefeller Plaza, Unit 1650, New York, NY 10020',
        },
        phone: { label: 'Phone', type: 'phone', defaultValue: '' },
        note: { label: 'Note', type: 'text', defaultValue: '' },
      },
    },
    'contactform.office_4': {
      label: 'Office 4',
      fields: {
        name: { label: 'Name', type: 'text', defaultValue: 'Dubai' },
        address: {
          label: 'Address',
          type: 'text',
          defaultValue: 'Emirates Financial North Towers, 704B, DIFC',
        },
        phone: { label: 'Phone', type: 'phone', defaultValue: '' },
        note: {
          label: 'Note',
          type: 'text',
          defaultValue:
            'American Landmark is represented in the United Arab Emirates by American Landmark Capital DIF and is regulated by the Dubai Financial Services Authority.',
        },
      },
    },
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
